import { urlValidator } from '@plugins/validator';

// Icons must exist in src/fontawesome/css (project kit subset).
export const learnerLinkPresets = {
  icons: {
    'fa-link': { label: 'Link' },
    'fa-lock': { label: 'Lock' },
    'fa-file-shield': { label: 'Shield' },
    'fa-file-lines': { label: 'Document' },
    'fa-clipboard-list': { label: 'Clipboard' },
    'fa-id-card': { label: 'ID card' },
    'fa-book-open': { label: 'Book' },
    'fa-graduation-cap': { label: 'Graduation cap' },
    'fa-award': { label: 'Medal' },
    'fa-chart-line': { label: 'Chart' },
    'fa-user': { label: 'User' },
    'fa-linkedin': { label: 'LinkedIn', brand: true },
    'fa-users': { label: 'Users' },
    'fa-globe': { label: 'Network' },
    'fa-video': { label: 'Video' },
    'fa-image': { label: 'Image' },
    'fa-headphones': { label: 'Audio' },
    'fa-envelope': { label: 'Email' },
    'fa-folder': { label: 'Folder' },
    'fa-download': { label: 'Download' },
    'fa-star': { label: 'Star' },
  },
  colors: {
    'brand-blue': { label: 'Blue', cssVar: '--kate-logo-blue' },
    'brand-purple': { label: 'Purple', cssVar: '--kate-logo-purple' },
    'brand-magenta': { label: 'Magenta', cssVar: '--kate-logo-magenta' },
    'brand-orange': { label: 'Orange', cssVar: '--kate-logo-orange' },
    'brand-lime-green': { label: 'Lime green', cssVar: '--kate-logo-lime-green' },
    'brand-green': { label: 'Green', cssVar: '--kate-logo-green' },
  },
  defaults: {
    icon: 'fa-link',
    color: 'brand-blue',
  },
};

export const learnerLinkIconPresets = Object.keys(learnerLinkPresets.icons);
export const learnerLinkColorPresets = Object.keys(learnerLinkPresets.colors);

export const learnerLinkColorCssVars = Object.fromEntries(
  Object.entries(learnerLinkPresets.colors).map(([color, { cssVar }]) => [color, cssVar]),
);

export const defaultLearnerLinkIcon = learnerLinkPresets.defaults.icon;
export const defaultLearnerLinkColor = learnerLinkPresets.defaults.color;

// Rules per link category. Values must match the categories accepted by the profile service.
// shareWithLearnerLock disables the share toggle and forces share_with_learner to shareWithLearner.
// icon and color are applied to the form when the category is picked.
export const learnerLinkCategories = {
  delivery_profile: {
    label: 'Delivery Profile',
    shareWithLearner: false,
    shareWithLearnerLock: true,
    icon: 'fa-graduation-cap',
    color: 'brand-magenta',
  },
  learning_support_plan: {
    label: 'Learning Support Plan',
    shareWithLearnerLock: false,
    icon: 'fa-star',
    color: 'brand-purple',
  },
  learning_support_one_page: {
    label: 'Learning Support One Page',
    shareWithLearnerLock: false,
    icon: 'fa-star',
    color: 'brand-orange',
  },
  key_dates_action_plan: {
    label: 'Key Dates Action Plan',
    shareWithLearnerLock: false,
    icon: 'fa-file-lines',
    color: 'brand-green',
  },
  learner_evidence_folder: {
    label: 'Learner Evidence Folder',
    shareWithLearnerLock: false,
    icon: 'fa-folder',
    color: 'brand-blue',
  },
};

export const learnerLinkUrlErrorMessage = 'Please enter a valid URL and it must start with https://';

export function normalizeLearnerLinkUrl(url) {
  return url?.trim() ?? '';
}

const learnerLinkHostnamePattern = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
const learnerLinkIpv4Pattern = /^(?:\d{1,3}\.){3}\d{1,3}$/;

function isValidLearnerLinkHostname(hostname) {
  if (!hostname) {
    return false;
  }

  if (learnerLinkIpv4Pattern.test(hostname)) {
    return hostname.split('.').every(octet => Number(octet) <= 255);
  }

  return learnerLinkHostnamePattern.test(hostname);
}

function invalidLearnerLinkUrl() {
  return { isValid: false, message: learnerLinkUrlErrorMessage };
}

export function validateLearnerLinkUrl(url) {
  const normalized = normalizeLearnerLinkUrl(url);
  if (!urlValidator(normalized).isValid) {
    return invalidLearnerLinkUrl();
  }

  const urlObj = new URL(normalized);
  if (urlObj.protocol !== 'https:' || !isValidLearnerLinkHostname(urlObj.hostname)) {
    return invalidLearnerLinkUrl();
  }

  return { isValid: true, message: '' };
}

export function isLearnerLinkUrlInputValid(url) {
  return validateLearnerLinkUrl(url).isValid;
}

function getLearnerLinkIconPrefix(icon) {
  return learnerLinkPresets.icons[icon]?.brand ? 'fab' : 'fas';
}

export function getLearnerLinkIconClasses(icon) {
  return [getLearnerLinkIconPrefix(icon), icon];
}

export function getLearnerLinkIconLabel(icon) {
  return learnerLinkPresets.icons[icon]?.label ?? 'Link';
}

export function getLearnerLinkColorLabel(color) {
  return learnerLinkPresets.colors[color]?.label ?? 'Blue';
}

export function createEmptyLearnerLinkForm() {
  return {
    label: '',
    url: '',
    icon: defaultLearnerLinkIcon,
    icon_color: defaultLearnerLinkColor,
    share_with_learner: false,
    link_category: null,
  };
}

export function learnerLinkFormFromRecord(link) {
  return {
    label: link.label || '',
    url: link.url,
    icon: link.icon,
    icon_color: link.icon_color,
    share_with_learner: link.share_with_learner,
    link_category: link.link_category ?? null,
  };
}

export function learnerLinkRequestBody(form) {
  const category = learnerLinkCategories[form.link_category];
  return {
    label: form.label?.trim() || '',
    url: form.url,
    icon: form.icon,
    icon_color: form.icon_color,
    share_with_learner: category?.shareWithLearnerLock ? category.shareWithLearner : form.share_with_learner,
    link_category: form.link_category ?? null,
  };
}
